
import React, { useEffect, useRef } from "react"
import { Box, Typography, Container, Grid } from "@mui/material"
import { styled } from "@mui/material/styles"
import { keyframes } from "@mui/system"
import Ellipse1 from "../../../assets/images/Ellipse 4 (1).png"
import Ellipse2 from "../../../assets/images/Ellipse 2.png"
import Ellipse3 from "../../../assets/images/Ellipse 1.png"
import Ellipse4 from "../../../assets/images/Ellipse 21 (1).png"
import Ellipse5 from "../../../assets/images/Ellipse 3.png"
import Ellipse6 from "../../../assets/images/Ellipse 6.png"
import Ellipse7 from "../../../assets/images/Ellipse 5 (1).png"
import Animatorr from "../../../components/Aminator/Animator"


// Styled components
const SectionContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(10, 2),
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(12, 2),
  },
}))

// const LearnMoreButton = styled(Button)(({ theme }) => ({
//   backgroundColor: "#2A2A2A",
//   color: "#FFFFFF",
//   padding: theme.spacing(1.5, 4),
//   borderRadius: 4,
//   textTransform: "none",
//   fontWeight: 500,
//   marginTop: theme.spacing(4),
//   "&:hover": {
//     backgroundColor: "#1A1A1A",
//   },
// }))

// Define keyframes for clockwise rotation
const rotateClockwise = keyframes`
  from {
    transform: translate(-50%, -50%) rotate(0deg) translateX(var(--orbit-radius)) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg) translateX(var(--orbit-radius)) rotate(-360deg);
  }
`

// Define keyframes for counter-clockwise rotation
const rotateCounterClockwise = keyframes`
  from {
    transform: translate(-50%, -50%) rotate(0deg) translateX(var(--orbit-radius)) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(-360deg) translateX(var(--orbit-radius)) rotate(360deg);
  }
`

// Styled components for rotating elements
const RotatingElement = styled(Box)(({ theme, direction, duration, delay, radius }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  "--orbit-radius": `${radius}px`,
  animation: `${direction === "clockwise" ? rotateClockwise : rotateCounterClockwise} ${duration}s linear infinite`,
  animationDelay: `${delay}s`,
}))

// Network visualization component
const NetworkVisualization = () => {
  const containerRef = useRef(null)
  const [containerSize, setContainerSize] = React.useState(500)

  useEffect(() => {
    if (containerRef.current) {
      const updateSize = () => {
        const width = containerRef.current.offsetWidth
        const height = containerRef.current.offsetHeight
        const size = Math.min(width, height)
        setContainerSize(size)
      }

      updateSize()
      window.addEventListener("resize", updateSize)
      return () => window.removeEventListener("resize", updateSize)
    }
  }, [])

  // Profile images with animation properties
  const profiles = [
    {
      id: 1,
      size: 120,
      zIndex: 10,
      radius: 0,
      direction: "clockwise",
      duration: 0,
      delay: 0,
      image: Ellipse1,
    }, // Center profile (no animation)
    {
      id: 2,
      size: 60,
      zIndex: 5,
      radius: containerSize * 0.25,
      direction: "clockwise",
      duration: 30,
      delay: 0,
      image: Ellipse2,
    },
    {
      id: 3,
      size: 70,
      zIndex: 5,
      radius: containerSize * 0.3,
      direction: "counterClockwise",
      duration: 35,
      delay: 5,
      image: Ellipse3,
    },
    {
      id: 4,
      size: 80,
      zIndex: 5,
      radius: containerSize * 0.35,
      direction: "clockwise",
      duration: 40,
      delay: 2,
      image: Ellipse4,
    },
    {
      id: 5,
      size: 65,
      zIndex: 5,
      radius: containerSize * 0.28,
      direction: "counterClockwise",
      duration: 32,
      delay: 8,
      image: Ellipse5,
    },
    {
      id: 6,
      size: 75,
      zIndex: 5,
      radius: containerSize * 0.32,
      direction: "clockwise",
      duration: 38,
      delay: 4,
      image: Ellipse6
    },
    {
      id: 7,
      size: 55,
      zIndex: 5,
      radius: containerSize * 0.22,
      direction: "counterClockwise",
      duration: 28,
      delay: 6,
      image: Ellipse7,
    },
  ]

  // Colored dots with animation properties
  const dots = [
    { id: 1, color: "#FF9800", size: 16, radius: containerSize * 0.38, direction: "clockwise", duration: 25, delay: 0 },
    {
      id: 2,
      color: "#7E57C2",
      size: 16,
      radius: containerSize * 0.42,
      direction: "counterClockwise",
      duration: 28,
      delay: 3,
    },
    { id: 3, color: "#F44336", size: 16, radius: containerSize * 0.36, direction: "clockwise", duration: 22, delay: 6 },
    {
      id: 4,
      color: "#9E9E9E",
      size: 16,
      radius: containerSize * 0.4,
      direction: "counterClockwise",
      duration: 30,
      delay: 9,
    },
    {
      id: 5,
      color: "#7E57C2",
      size: 16,
      radius: containerSize * 0.34,
      direction: "clockwise",
      duration: 26,
      delay: 12,
    },
  ]

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: 500,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Background circle */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: containerSize,
          height: containerSize,
          borderRadius: "50%",
          backgroundColor: "#F5F5F5",
        }}
      />

      {/* Dashed circular lines */}
      {[1, 2, 3].map((circle) => (
        <Box
          key={`circle-${circle}`}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: `${containerSize * (1 - 0.2 * circle)}px`,
            height: `${containerSize * (1 - 0.2 * circle)}px`,
            borderRadius: "50%",
            border: "2px dashed #CCCCCC",
          }}
        />
      ))}

      {/* Profile images */}
      {profiles.map((profile) => (
        <RotatingElement
          key={`profile-${profile.id}`}
          direction={profile.direction}
          duration={profile.duration}
          delay={profile.delay}
          radius={profile.radius}
        >
          <Box
            sx={{
              width: profile.size,
              height: profile.size,
              borderRadius: "50%",
              overflow: "hidden",
              border: "3px solid #FFFFFF",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              zIndex: profile.zIndex,
              transform: profile.radius > 0 ? "translate(-50%, -50%)" : "translate(-50%, -50%)",
            }}
          >
            <Box
              component="img"
              src={profile.image}
              alt={`Profile ${profile.id}`}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
        </RotatingElement>
      ))}

      {/* Colored dots */}
      {dots.map((dot) => (
        <RotatingElement
          key={`dot-${dot.id}`}
          direction={dot.direction}
          duration={dot.duration}
          delay={dot.delay}
          radius={dot.radius}
        >
          <Box
            sx={{
              width: dot.size,
              height: dot.size,
              backgroundColor: dot.color,
              borderRadius: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2,
            }}
          />
        </RotatingElement>
      ))}

      {/* User card */}
      {/* <Box
        sx={{
          position: "absolute",
          top: "15%",
          left: "30%",
          backgroundColor: "#FFFFFF",
          borderRadius: 1,
          padding: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          zIndex: 15,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src="/images/user-card.jpg"
            alt="User"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 100,
              height: 10,
              backgroundColor: "#E0E0E0",
              borderRadius: 1,
              mb: 0.5,
            }}
          />
          <Box
            sx={{
              width: 70,
              height: 8,
              backgroundColor: "#E0E0E0",
              borderRadius: 1,
            }}
          />
        </Box>
      </Box> */}
    </Box>
  )
}

// Simple Animator component
const Animator = ({ children, variant = "fade", delay = 0, timeout = 300, direction = "up" }) => {
  return <>{children}</>
}

function CommunitySection() {
  const useIsMobile = () => {
    const [isMobile, setIsMobile] = React.useState(false)

    React.useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 960)
      }

      checkMobile()
      window.addEventListener("resize", checkMobile)
      return () => window.removeEventListener("resize", checkMobile)
    }, [])

    return isMobile
  }

  const mobile = useIsMobile()

  return (
    <SectionContainer maxWidth="lg">
      <Grid container spacing={6} alignItems="center">
        {/* Left Column - Text Content */}
        <Grid item xs={12} md={5}>
          {/* <Animator variant="fade" delay={300} timeout={500}> */}
            <Animatorr variant="slide" timeout={800} delay={200}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: "Monserrat, sans-serif",
                fontSize: mobile ? "2rem" : "2.5rem",
                fontWeight: 700,
                color: "#333333",
                lineHeight: 1.2,
                mb: 3,
              }}
            >
              Trusted by hundreds of
              Employers and Professionals
            </Typography>
          </Animatorr>

          <Animatorr variant="slide" timeout={800} delay={400}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Monserrat, sans-serif",
                color: "#555555",
                lineHeight: 1.7,
                fontSize: "1rem",
              }}
            >
              Businesses worldwide rely on us to find and hire exceptional talent. While the best talents trust us to match them with the best fit for their skills. Our growing network spans diverse industries—from tech startups to global enterprises, helping companies of all sizes reach new heights.
            </Typography>
          </Animatorr>

          {/* <Animator variant="fade" delay={500} timeout={500}>
            <LearnMoreButton variant="contained">Learn More</LearnMoreButton>
          </Animator> */}
        </Grid>

        {/* Right Column - Network Visualization */}
        <Grid item xs={12} md={7}>
          <Animator variant="fade" direction="left" delay={600} timeout={500}>
            <NetworkVisualization />
          </Animator>
        </Grid>
      </Grid>
    </SectionContainer>
  )
}

export default CommunitySection

